import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Grid, CircularProgress,
} from "@material-ui/core";
import ReactSelect from "react-select";
import SchoolService from "../../../_services/school.service";

export interface Lookup { id: number; name: string }
export interface TagTypeLookup { id: number; name: string; tags: Lookup[] }
export interface SchoolLookups {
  franchises: Lookup[];
  mediums: Lookup[];
  classifications: Lookup[];
  statuses: Lookup[];
  cities: (Lookup & { state?: string | null })[];
  courses: Lookup[];
  programs: Lookup[];
  tagTypes: TagTypeLookup[];
}

interface Props {
  open: boolean;
  schoolId: number | null; // null → create
  lookups: SchoolLookups | null;
  readOnly?: boolean; // view mode
  onClose: (saved?: boolean) => void;
  onError: (msg: string) => void;
}

type Option = { value: number; label: string };
const opt = (l: Lookup): Option => ({ value: l.id, label: l.name });
const portalTarget = typeof document !== "undefined" ? document.body : undefined;

const rsStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
  control: (base: any) => ({ ...base, minHeight: 40, borderRadius: 8 }),
};

const emptyForm = {
  name: "", schoolCode: "", displayNameForGradeCard: "",
  franchiseId: "" as number | "", mediumId: "" as number | "",
  schoolClassificationId: "" as number | "", cityId: "" as number | "",
  statusId: "" as number | "",
  location: "", emailId: "", contactPerson: "", contactNumber: "",
  schoolAddress: "", url: "", schoolOpeningDate: "", remarks: "",
  creditLimit: "" as number | "", sortOrder: "" as number | "",
};

const SchoolFormDialog: React.FC<Props> = ({ open, schoolId, lookups, readOnly = false, onClose, onError }) => {
  const editMode = schoolId != null;
  const roInput = readOnly ? { readOnly: true } : undefined;
  const [form, setForm] = useState(emptyForm);
  const [courseIds, setCourseIds] = useState<number[]>([]);
  const [programIds, setProgramIds] = useState<number[]>([]);
  const [tagSel, setTagSel] = useState<Record<number, number | null>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Fallback: if the parent didn't supply lookups (its fetch failed/raced),
  // load them here so the form is always self-sufficient.
  const [localLookups, setLocalLookups] = useState<SchoolLookups | null>(null);
  const lk = lookups || localLookups;

  useEffect(() => {
    if (open && !lookups && !localLookups) {
      SchoolService.lookups()
        .then((res) => { if (res.data?.success) setLocalLookups(res.data.data); })
        .catch((err: any) => onError(err?.response?.data?.error?.message || "Failed to load form options"));
    }
  }, [open, lookups, localLookups, onError]);

  // Load the school (edit) or reset (create) whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setError("");
    if (!editMode) {
      setForm({
        ...emptyForm,
        // Auto-select the only franchise when there is just one.
        franchiseId: lk?.franchises.length === 1 ? lk.franchises[0].id : "",
      });
      setCourseIds([]);
      setProgramIds([]);
      setTagSel({});
      return;
    }
    setLoading(true);
    SchoolService.get(schoolId!)
      .then((res) => {
        const s = res.data?.data;
        if (!s) { onError("Failed to load school"); return; }
        setForm({
          name: s.name || "", schoolCode: s.schoolCode || "",
          displayNameForGradeCard: s.displayNameForGradeCard || "",
          franchiseId: s.franchiseId ?? "", mediumId: s.mediumId ?? "",
          schoolClassificationId: s.schoolClassificationId ?? "", cityId: s.cityId ?? "",
          statusId: s.statusId ?? "",
          location: s.location || "", emailId: s.emailId || "",
          contactPerson: s.contactPerson || "", contactNumber: s.contactNumber || "",
          schoolAddress: s.schoolAddress || "", url: s.url || "",
          schoolOpeningDate: (s.schoolOpeningDate || "").slice(0, 10), remarks: s.remarks || "",
          creditLimit: s.creditLimit ?? "", sortOrder: s.sortOrder ?? "",
        });
        setCourseIds(s.courseIds || []);
        setProgramIds(s.programIds || []);
        // Map each selected tagId back to its tag type.
        const sel: Record<number, number | null> = {};
        (lk?.tagTypes || []).forEach((tt) => {
          const match = tt.tags.find((t) => (s.tagIds || []).includes(t.id));
          sel[tt.id] = match ? match.id : null;
        });
        setTagSel(sel);
      })
      .catch((err: any) => onError(err?.response?.data?.error?.message || "Failed to load school"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, schoolId, editMode, lk]);

  const set = (field: string, value: any) => setForm((f) => ({ ...f, [field]: value }));

  const cityOptions = useMemo(
    () => (lk?.cities || []).map((c) => ({ value: c.id, label: c.state ? `${c.name} (${c.state})` : c.name })),
    [lk],
  );
  const courseOptions = useMemo(() => (lk?.courses || []).map(opt), [lk]);
  const programOptions = useMemo(() => (lk?.programs || []).map(opt), [lk]);

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (!form.franchiseId) { setError("Select a franchise"); return; }

    setSaving(true);
    setError("");
    try {
      const tagIds = Object.values(tagSel).filter((v): v is number => v != null);
      const payload = {
        ...form,
        franchiseId: form.franchiseId || null,
        mediumId: form.mediumId || null,
        schoolClassificationId: form.schoolClassificationId || null,
        cityId: form.cityId || null,
        statusId: form.statusId || null,
        creditLimit: form.creditLimit === "" ? null : Number(form.creditLimit),
        sortOrder: form.sortOrder === "" ? null : Number(form.sortOrder),
        courseIds, programIds, tagIds,
      };
      const res = editMode
        ? await SchoolService.update(schoolId!, payload)
        : await SchoolService.create(payload);
      if (res.data?.success) onClose(true);
      else setError(res.data?.error?.message || res.data?.message || "Failed to save");
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.response?.data?.message || "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  // A single-select rendered with react-select (external label — avoids the MUI
  // native-select floating-label overlap and clearly shows the option list).
  const singleSelect = (
    label: string, field: keyof typeof emptyForm, options: Lookup[], required = false,
  ) => {
    const opts = options.map(opt);
    return (
      <>
        <label className="school-form-field-label">{label}{required ? " *" : ""}</label>
        <ReactSelect
          isClearable
          isDisabled={readOnly}
          placeholder={`Select ${label.toLowerCase()}…`}
          menuPortalTarget={portalTarget}
          styles={rsStyles}
          options={opts}
          value={opts.find((o) => o.value === form[field]) || null}
          onChange={(o: any) => set(field as string, o ? o.value : "")}
        />
      </>
    );
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{readOnly ? "View School" : editMode ? "Edit School" : "Add School"}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}><CircularProgress size={28} /></div>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">Name *</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} placeholder="School name"
                value={form.name} onChange={(e) => set("name", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">School Code</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} placeholder="Code"
                value={form.schoolCode} onChange={(e) => set("schoolCode", e.target.value)} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">Display Name (grade card)</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} placeholder="Display name"
                value={form.displayNameForGradeCard} onChange={(e) => set("displayNameForGradeCard", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              {singleSelect("Franchise", "franchiseId", lk?.franchises || [], true)}
            </Grid>

            <Grid item xs={12} sm={4}>{singleSelect("Medium", "mediumId", lk?.mediums || [])}</Grid>
            <Grid item xs={12} sm={4}>{singleSelect("Classification", "schoolClassificationId", lk?.classifications || [])}</Grid>
            <Grid item xs={12} sm={4}>{singleSelect("Status", "statusId", lk?.statuses || [])}</Grid>

            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">City</label>
              <ReactSelect
                isClearable
                isDisabled={readOnly}
                placeholder="Search city…"
                menuPortalTarget={portalTarget}
                styles={rsStyles}
                options={cityOptions}
                value={cityOptions.find((c) => c.value === form.cityId) || null}
                onChange={(o: any) => set("cityId", o ? o.value : "")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">E-Mail ID</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} placeholder="email@example.com"
                value={form.emailId} onChange={(e) => set("emailId", e.target.value)} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">Contact Person</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} placeholder="Contact person"
                value={form.contactPerson} onChange={(e) => set("contactPerson", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">Contact Mobile</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} placeholder="Mobile number"
                value={form.contactNumber} onChange={(e) => set("contactNumber", e.target.value)} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">Location</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} placeholder="Location"
                value={form.location} onChange={(e) => set("location", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">URL</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} placeholder="https://…"
                value={form.url} onChange={(e) => set("url", e.target.value)} />
            </Grid>

            <Grid item xs={12} sm={4}>
              <label className="school-form-field-label">Opening Date</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} type="date"
                InputLabelProps={{ shrink: true }}
                value={form.schoolOpeningDate} onChange={(e) => set("schoolOpeningDate", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <label className="school-form-field-label">Credit Limit</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} type="number" placeholder="0"
                value={form.creditLimit} onChange={(e) => set("creditLimit", e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <label className="school-form-field-label">Sort Order</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} type="number" placeholder="0"
                value={form.sortOrder} onChange={(e) => set("sortOrder", e.target.value)} />
            </Grid>

            <Grid item xs={12}>
              <label className="school-form-field-label">Address</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} multiline rows={2} placeholder="Address"
                value={form.schoolAddress} onChange={(e) => set("schoolAddress", e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <label className="school-form-field-label">Remarks</label>
              <TextField fullWidth size="small" variant="outlined" InputProps={roInput} multiline rows={2} placeholder="Remarks"
                value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
            </Grid>

            {/* School hierarchy tags — one dropdown per school-applicable tag type */}
            {(lk?.tagTypes || []).length > 0 && (
              <Grid item xs={12}>
                <div className="school-form-section-title">School Hierarchy</div>
              </Grid>
            )}
            {(lk?.tagTypes || []).map((tt) => (
              <Grid item xs={12} sm={6} key={tt.id}>
                <label className="school-form-field-label">{tt.name}</label>
                <ReactSelect
                  isClearable
                  isDisabled={readOnly}
                  placeholder={`Select ${tt.name}…`}
                  menuPortalTarget={portalTarget}
                  styles={rsStyles}
                  options={tt.tags.map(opt)}
                  value={tt.tags.map(opt).find((o) => o.value === tagSel[tt.id]) || null}
                  onChange={(o: any) => setTagSel((prev) => ({ ...prev, [tt.id]: o ? o.value : null }))}
                />
              </Grid>
            ))}

            {/* Courses & Programs — both are shown; a school can map either or both */}
            <Grid item xs={12}>
              <div className="school-form-section-title">Courses &amp; Programs</div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">Courses</label>
              <ReactSelect
                isMulti closeMenuOnSelect={false}
                isDisabled={readOnly}
                placeholder="Select courses…"
                menuPortalTarget={portalTarget}
                styles={rsStyles}
                options={courseOptions}
                value={courseOptions.filter((o) => courseIds.includes(o.value))}
                onChange={(vals: any) => setCourseIds((vals || []).map((v: Option) => v.value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <label className="school-form-field-label">Programs</label>
              <ReactSelect
                isMulti closeMenuOnSelect={false}
                isDisabled={readOnly}
                placeholder="Select programs…"
                menuPortalTarget={portalTarget}
                styles={rsStyles}
                options={programOptions}
                value={programOptions.filter((o) => programIds.includes(o.value))}
                onChange={(vals: any) => setProgramIds((vals || []).map((v: Option) => v.value))}
              />
            </Grid>

            {error && (
              <Grid item xs={12}>
                <div style={{ color: "var(--status-danger)", fontSize: 13 }}>{error}</div>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>{readOnly ? "Close" : "Cancel"}</Button>
        {!readOnly && (
          <Button onClick={handleSave} color="primary" variant="contained" disabled={saving || loading}>
            {saving ? <CircularProgress size={20} /> : editMode ? "Update" : "Create"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SchoolFormDialog;
